import { PillarsBetaLogo } from "@/components/pillars-beta-logo"
import { Button } from "@/components/ui/button"
import { auth, loginWithApple, loginWithGoogle } from "@/lib/firebase"
import { useEffect, useRef, useState } from "react"
import { useAuthState } from "react-firebase-hooks/auth"
import { useSearch } from "@tanstack/react-router"
import { useToast } from "@/components/ui/use-toast"
import { captureError, trackEvent } from "@/lib/log"

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
const FRONTEND_BASE_URL = import.meta.env.VITE_FRONTEND_BASE_URL || window.location.origin

export function AlexaLink() {
  const query = useSearch({ strict: false }) as Record<string, string>
  const { toast } = useToast()

  const [firebaseUser, firebaseLoading] = useAuthState(auth)
  const [authCode, setAuthCode] = useState<string | null>(null)
  const [browserToken, setBrowserToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  const redirectUri = query.redirect_uri
  const state = query.state
  const clientId = query.client_id
  const codeChallenge = query.code_challenge
  const codeChallengeMethod = query.code_challenge_method
  const app = query.app

  const viewTracked = useRef(false)
  useEffect(() => {
    if (viewTracked.current) return
    viewTracked.current = true
    trackEvent("opened the Alexa account linking page", { app, clientId })
  }, [app, clientId])

  const signInShownTracked = useRef(false)
  useEffect(() => {
    if (!firebaseLoading && !firebaseUser && !signInShownTracked.current) {
      signInShownTracked.current = true
      trackEvent("was shown the Alexa link sign in", { app })
    }
  }, [firebaseLoading, firebaseUser, app])

  const successShownTracked = useRef(false)
  useEffect(() => {
    if (authCode && app === "amazon" && !successShownTracked.current) {
      successShownTracked.current = true
      trackEvent("was shown the Alexa link success screen")
    }
  }, [authCode, app])

  const authorizeStarted = useRef(false)
  useEffect(() => {
    if (firebaseUser && !authCode && !authorizeStarted.current) {
      authorizeStarted.current = true
      setLoading(true)

      firebaseUser
        .getIdToken()
        .then((idToken) =>
          fetch(`${API_BASE_URL}/oauth/authorize`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              firebaseToken: idToken,
              clientId,
              redirectUri,
              state,
              codeChallenge,
              codeChallengeMethod,
            }),
          })
        )
        .then((res) => res.json())
        .then((data) => {
          setAuthCode(data.authCode)
          setBrowserToken(data.browserToken)
          setLoading(false)
        })
        .catch((error) => {
          captureError(error, { tags: { source: "alexa_link_authorize" } })
          setLoading(false)
          toast({
            variant: "destructive",
            title: "Failed to authorize. Please try again.",
          })
          authorizeStarted.current = false
        })
    }
  }, [firebaseUser, authCode, clientId, redirectUri, state, codeChallenge, codeChallengeMethod, toast])

  const FRONTEND_BASE_URL_NO_SLASH = FRONTEND_BASE_URL.replace(/\/$/, "")
  const browserLink = browserToken
    ? `${FRONTEND_BASE_URL_NO_SLASH}/?token=${browserToken}`
    : null

  async function handleCopyLink() {
    if (!browserLink) return
    trackEvent("copied Alexa link")
    let ok = false
    const hadAsyncClipboard = Boolean(navigator.clipboard?.writeText)
    let execCommandResult: boolean | null = null
    try {
      if (hadAsyncClipboard) {
        await navigator.clipboard.writeText(browserLink)
        ok = true
      }
    } catch (error) {
      captureError(error, { tags: { source: "alexa_link_copy_async" } })
    }
    if (!ok) {
      try {
        const span = document.createElement("span")
        span.textContent = browserLink
        span.contentEditable = "true"
        span.style.position = "fixed"
        span.style.left = "-9999px"
        span.style.top = "0"
        span.style.opacity = "0"
        span.style.whiteSpace = "pre"
        document.body.appendChild(span)
        const range = document.createRange()
        range.selectNodeContents(span)
        const selection = window.getSelection()
        selection?.removeAllRanges()
        selection?.addRange(range)
        execCommandResult = document.execCommand("copy")
        ok = execCommandResult
        selection?.removeAllRanges()
        document.body.removeChild(span)
      } catch (error) {
        captureError(error, { tags: { source: "alexa_link_copy_exec" } })
      }
    }
    if (ok) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } else {
      captureError(new Error("Alexa link copy failed silently"), {
        tags: { source: "alexa_link_copy_failed" },
        extra: {
          hadAsyncClipboard,
          execCommandResult,
          isSecureContext: window.isSecureContext,
          userAgent: navigator.userAgent,
        },
      })
      toast({
        variant: "destructive",
        title: "Couldn't copy automatically",
        description: "Please try the copy button again.",
      })
    }
  }

  function handleCloseAndContinue() {
    if (redirectUri && authCode) {
      trackEvent("closed and continued Alexa linking")
      const url = new URL(redirectUri)
      url.searchParams.set("code", authCode)
      if (state) url.searchParams.set("state", state)
      window.location.href = url.toString()
    }
  }

  if (firebaseLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    )
  }

  if (!firebaseUser) {
    return (
      <div
        className="bg-[#afb9c3] w-screen h-screen flex p-5 justify-center items-center bg-cover md:bg-contain bg-no-repeat"
        style={{
          backgroundImage: `url(https://cdn.prod.website-files.com/604d480efd2aff6558953436/65f50333ccd81bc76a406d62_Background.png)`,
        }}
      >
        <div className="flex flex-col gap-5 items-center mb-20">
          <PillarsBetaLogo white />
          <div className="text-white text-center text-sm mb-2">
            Sign in to link your Alexa
          </div>
          <div className="flex gap-5">
            <div className="w-1 bg-white rounded-full"></div>
            <div className="flex flex-col gap-2 min-w-[300px]">
              <Button
                onClick={() => { trackEvent("attempted to log in via Alexa link", { provider: "apple" }); loginWithApple() }}
                className="flex items-center gap-2 bg-white hover:bg-gray-100 hover:translate-x-1 transition-all duration-300 py-6"
              >
                <img src="/apple-logo.svg" className="h-6 mb-[2px]" />
                <div className="text-lg text-black">Continue with Apple</div>
              </Button>
              <Button
                onClick={() => { trackEvent("attempted to log in via Alexa link", { provider: "google" }); loginWithGoogle() }}
                className="flex items-center gap-2 bg-white hover:bg-gray-100 hover:translate-x-1 transition-all duration-300 py-6"
              >
                <img src="/google-logo.svg" className="h-6" />
                <div className="text-lg text-gray-500">
                  Continue with Google
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (authCode) {
    if (app === "amazon") {
      return (
        <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 p-5 overflow-y-auto">
          <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow-sm border flex flex-col gap-6">
            <PillarsBetaLogo />
            <div className="text-primary font-medium text-xl">
              Account linked successfully!
            </div>
            <div className="text-sm text-gray-600">
              You will need to continue setup in your browser after closing this
              window.
            </div>
            <div className="flex flex-col gap-3">
              <div className="text-sm text-gray-600">
                First copy our website link
              </div>
              <div
                onClick={handleCopyLink}
                className="w-full min-w-0 p-3 rounded-xl bg-primary/5 border border-primary/20 text-sm font-medium truncate select-all cursor-pointer"
                style={{ WebkitUserSelect: "all", userSelect: "all", whiteSpace: "nowrap" }}
              >
                <span className="text-primary">{FRONTEND_BASE_URL_NO_SLASH.replace(/^https?:\/\//, "")}</span>
                {browserToken && (
                  <span className="text-transparent">{`/?token=${browserToken}`}</span>
                )}
              </div>
              <Button onClick={handleCopyLink} className="w-full">
                {copied ? "Copied!" : "1. Click to copy"}
              </Button>
            </div>
            <div className="flex flex-col gap-3">
              <div className="text-sm text-gray-600">
                Then close this window
              </div>
              <Button onClick={handleCloseAndContinue} className="w-full">
                2. Save and close
              </Button>
            </div>
          </div>
        </div>
      )
    }

    handleCloseAndContinue()
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    )
  }

  return null
}
